/**
 * Intent espèce dans la barre de recherche Booking.
 * Les synonymes chat/félin ne mappent plus vers le métier `education-feline`.
 */

export const SPECIES_IDS = {
  DOG: 1,
  CAT: 2,
} as const;

export type SpeciesSearchIntent = {
  speciesId: number | null;
  /** Métier déduit pour les phrases legacy (« éducation féline »). */
  professionSlugHint: string | null;
  /** Texte sans tokens espèce, pour déduire le métier. */
  textForProfession: string;
};

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/** Phrases exactes (legacy métier félin → espèce + éducateur). */
const EXACT_PHRASE_INTENTS: Record<
  string,
  { speciesId: number; professionSlug?: string }
> = {
  'education feline': { speciesId: SPECIES_IDS.CAT, professionSlug: 'trainers' },
  'educateur felin': { speciesId: SPECIES_IDS.CAT, professionSlug: 'trainers' },
  'educateur feline': { speciesId: SPECIES_IDS.CAT, professionSlug: 'trainers' },
};

/** Tokens espèce (mots / bigrammes) → species.id — pas « canin » (dans libellés métier). */
const SPECIES_TOKENS: { token: string; speciesId: number }[] = [
  { token: 'education feline', speciesId: SPECIES_IDS.CAT },
  { token: 'chat', speciesId: SPECIES_IDS.CAT },
  { token: 'felin', speciesId: SPECIES_IDS.CAT },
  { token: 'feline', speciesId: SPECIES_IDS.CAT },
  { token: 'cat', speciesId: SPECIES_IDS.CAT },
  { token: 'chien', speciesId: SPECIES_IDS.DOG },
  { token: 'dog', speciesId: SPECIES_IDS.DOG },
].sort((a, b) => b.token.length - a.token.length);

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Retire un token (espaces normalisés) du texte. */
function stripToken(normalizedText: string, token: string): string {
  const pattern = new RegExp(`(?:^|\\s)${escapeRegExp(token)}(?=\\s|$)`, 'g');
  return normalizedText.replace(pattern, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Déduit espèce (+ hint métier) depuis la saisie libre « Quoi ».
 */
export function resolveSpeciesSearchIntent(text: string): SpeciesSearchIntent {
  const raw = text.trim();
  if (!raw) {
    return { speciesId: null, professionSlugHint: null, textForProfession: '' };
  }

  const normalized = normalize(raw);
  const exact = EXACT_PHRASE_INTENTS[normalized];
  if (exact) {
    return {
      speciesId: exact.speciesId,
      professionSlugHint: exact.professionSlug ?? null,
      textForProfession: exact.professionSlug ? '' : normalized,
    };
  }

  let speciesId: number | null = null;
  let remaining = normalized;
  for (const { token, speciesId: sid } of SPECIES_TOKENS) {
    if (remaining === token || remaining.includes(` ${token} `) || remaining.startsWith(`${token} `) || remaining.endsWith(` ${token}`)) {
      speciesId = sid;
      remaining = stripToken(remaining, token);
      break;
    }
  }

  // « canin » dans « educateur canin » : on retire le token espèce pour le métier
  // mais on ne force pas speciesId chien si déjà déduit ailleurs — déjà géré.

  return {
    speciesId,
    professionSlugHint: null,
    textForProfession: remaining || (speciesId ? '' : normalized),
  };
}
